import { Modal } from 'antd';
import { useMemo, useEffect } from 'react';
import {
  FileText,
  X,
  AlertCircle,
  Lightbulb,
  Target,
  TrendingUp,
} from 'lucide-react';
import TextEditor from './TextEditor';
import MarkdownEditor from './MarkdownEditor';
import DocxEditor from './DocxEditor';
import PdfEditor from './PdfEditor';
import type { ResumeOptimizeResult } from '../types/resumeOptimize';
import {
  getPriorityInfo,
  getCompetitivenessText,
} from '../services/resumeOptimizeService';

interface ResumeEditorModalProps {
  file: File | null;
  isOpen: boolean;
  onClose: () => void;
  evaluationResult?: ResumeOptimizeResult | null;
  showEvaluationSidebar?: boolean;
}

// 评估结果侧边栏组件
function EvaluationSidebar({
  result,
}: {
  result: ResumeOptimizeResult | null | undefined;
}) {
  if (!result) {
    return (
      <div className="w-80 h-full bg-slate-800/50 border-r border-slate-700 p-4 overflow-y-auto">
        <div className="text-center text-slate-500 mt-8">
          <Lightbulb className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>暂无评估结果</p>
          <p className="text-sm mt-1">请先进行简历评估</p>
        </div>
      </div>
    );
  }

  // 按优先级排序建议
  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  const sortedSuggestions = [...result.suggestions].sort(
    (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority],
  );

  // 根据分数获取颜色 - 与h5端保持一致
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 80) return 'text-amber-400';
    if (score >= 70) return 'text-yellow-400';
    if (score >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  // 根据匹配度获取进度条颜色 - 与h5端保持一致
  const getMatchBarColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 80) return 'bg-amber-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 60) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="w-80 h-full bg-slate-800/50 border-r border-slate-700 overflow-y-auto">
      {/* 综合评分 */}
      <div className="p-4 border-b border-slate-700">
        <div className="text-center">
          <div
            className={`text-3xl font-bold ${getScoreColor(result.scores.overall)}`}
          >
            {Math.ceil(result.scores.overall)}
          </div>
          <div className="text-sm text-slate-400 mt-1">综合评分</div>
        </div>
        {/* 分项评分 */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">结构完整性</span>
            <span
              className={`font-medium ${getScoreColor(result.scores.structure)}`}
            >
              {result.scores.structure}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">内容质量</span>
            <span
              className={`font-medium ${getScoreColor(result.scores.content)}`}
            >
              {result.scores.content}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">表达规范</span>
            <span
              className={`font-medium ${getScoreColor(result.scores.expression)}`}
            >
              {result.scores.expression}
            </span>
          </div>
          {result.scores.jdMatch !== undefined && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">岗位匹配度</span>
              <span
                className={`font-medium ${getScoreColor(result.scores.jdMatch)}`}
              >
                {result.scores.jdMatch}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 主要问题 */}
      {result.mainIssues.length > 0 && (
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-amber-400" />
            <h4 className="text-white font-medium text-sm">主要问题</h4>
            <span className="text-slate-500 text-xs">
              ({result.mainIssues.length})
            </span>
          </div>
          <ul className="space-y-2">
            {result.mainIssues.slice(0, 5).map((issue, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 text-xs flex items-center justify-center">
                  {index + 1}
                </span>
                <span className="text-slate-300 leading-relaxed">{issue}</span>
              </li>
            ))}
            {result.mainIssues.length > 5 && (
              <li className="text-slate-500 text-xs text-center">
                还有 {result.mainIssues.length - 5} 个问题...
              </li>
            )}
          </ul>
        </div>
      )}

      {/* 优化建议 */}
      {sortedSuggestions.length > 0 && (
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-amber-400" />
            <h4 className="text-white font-medium text-sm">优化建议</h4>
            <span className="text-slate-500 text-xs">
              ({sortedSuggestions.length})
            </span>
          </div>
          <div className="space-y-3">
            {sortedSuggestions.slice(0, 3).map((suggestion, index) => {
              const priorityInfo = getPriorityInfo(suggestion.priority);
              return (
                <div key={index} className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-white text-sm font-medium">
                      {suggestion.category}
                    </span>
                    <span
                      className={`px-1.5 py-0.5 rounded text-xs ${priorityInfo.bgColor} ${priorityInfo.color}`}
                    >
                      {priorityInfo.text}
                    </span>
                  </div>
                  <p className="text-slate-400 text-xs mb-1">
                    <span className="text-slate-500">问题：</span>
                    {suggestion.issue}
                  </p>
                  <p className="text-slate-300 text-xs">
                    <span className="text-slate-500">建议：</span>
                    {suggestion.advice}
                  </p>
                </div>
              );
            })}
            {sortedSuggestions.length > 3 && (
              <div className="text-slate-500 text-xs text-center">
                还有 {sortedSuggestions.length - 3} 条建议...
              </div>
            )}
          </div>
        </div>
      )}

      {/* 岗位匹配分析 */}
      {result.jdAnalysis && (
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-emerald-400" />
            <h4 className="text-white font-medium text-sm">岗位匹配分析</h4>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-slate-400 text-sm">整体匹配度</span>
              <span
                className={`font-medium ${getScoreColor(result.jdAnalysis.matchPercentage)}`}
              >
                {result.jdAnalysis.matchPercentage}%
              </span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getMatchBarColor(result.jdAnalysis.matchPercentage)}`}
                style={{ width: `${result.jdAnalysis.matchPercentage}%` }}
              />
            </div>
            {/* 关键词覆盖 */}
            {result.jdAnalysis.coveredKeywords.length > 0 && (
              <div>
                <span className="text-slate-500 text-xs block mb-2">
                  已覆盖关键词：
                </span>
                <div className="flex flex-wrap gap-1">
                  {result.jdAnalysis.coveredKeywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* 缺失关键词 */}
            {result.jdAnalysis.missingKeywords.length > 0 && (
              <div>
                <span className="text-slate-500 text-xs block mb-2">
                  缺失关键词：
                </span>
                <div className="flex flex-wrap gap-1">
                  {result.jdAnalysis.missingKeywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded text-xs"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Gap分析 */}
            {result.jdAnalysis.gapAnalysis.length > 0 && (
              <div>
                <span className="text-slate-500 text-xs block mb-2">
                  Gap分析：
                </span>
                <ul className="space-y-1">
                  {result.jdAnalysis.gapAnalysis
                    .slice(0, 3)
                    .map((gap, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-1 text-xs text-slate-400"
                      >
                        <span
                          className="text-slate-600 mt-0.5"
                          style={{ position: 'relative', top: '-3px' }}
                        >
                          •
                        </span>
                        <span>{gap}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ResumeEditorModal({
  file,
  isOpen,
  onClose,
  evaluationResult,
  showEvaluationSidebar = true,
}: ResumeEditorModalProps) {
  const fileType = useMemo(() => {
    if (!file) return null;
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ext;
  }, [file]);

  // 处理关闭弹窗，确保恢复页面滚动
  const handleClose = () => {
    onClose();
    // 强制恢复 body 滚动
    setTimeout(() => {
      document.body.style.overflow = '';
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
    }, 0);
  };

  const renderEditor = () => {
    if (!file) return null;

    switch (fileType) {
      case 'md':
        return <MarkdownEditor file={file} onClose={handleClose} />;
      case 'txt':
        return <TextEditor file={file} onClose={handleClose} />;
      case 'docx':
        return <DocxEditor file={file} onClose={handleClose} />;
      case 'pdf':
        return <PdfEditor file={file} onClose={handleClose} />;
      default:
        return (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <FileText className="w-16 h-16 mb-4 opacity-50" />
            <p>暂不支持该格式的在线编辑</p>
            <p className="text-sm mt-2">当前支持：TXT、MD、DOCX、PDF 格式</p>
          </div>
        );
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      width="100%"
      style={{ maxWidth: '100vw', top: 0, padding: 0 }}
      footer={null}
      closable={false}
      maskClosable={false}
      className="resume-editor-modal fullscreen-modal"
      styles={{
        container: {
          background: '#0F172A',
          border: 'none',
          borderRadius: 0,
          padding: 0,
          overflow: 'hidden',
          boxShadow: 'none',
          height: '100vh',
          maxHeight: '100vh',
          width: '100vw',
          maxWidth: '100vw',
        },
        body: {
          padding: 0,
          background: '#0F172A',
          height: '100vh',
          overflow: 'hidden',
        },
        mask: {
          backgroundColor: '#0F172A',
        },
        wrapper: {
          background: 'transparent',
          overflow: 'hidden',
        },
      }}
      title={null}
      destroyOnClose={true}
    >
      <div className="flex h-full">
        {/* 左侧评估结果 */}
        {showEvaluationSidebar && (
          <EvaluationSidebar result={evaluationResult} />
        )}

        {/* 右侧编辑器区域 */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* 自定义头部 */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-slate-800/50 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">编辑简历</h3>
                <p className="text-slate-500 text-sm">{file?.name}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-slate-500 hover:text-amber-400 hover:bg-amber-500/20 rounded-lg transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* 内容区域 */}
          <div className="flex-1 overflow-auto p-6 bg-[#0F172A]">
            {renderEditor()}
          </div>
        </div>
      </div>
    </Modal>
  );
}
